import { useEffect, useState, useMemo } from "react";
import { stripePromise } from "../../../constants";
import {Typography, Grid, CircularProgress, FormControlLabel, Checkbox, Box, Button, Chip, Paper, Stack, Divider, Tooltip, IconButton, Alert, useTheme} from "@mui/material";
import { ShoppingCart, Info, Check, Droplets, FlaskConical } from "lucide-react";
import PageProtectedOnboarding from "../../../components/Page/PageProtectedOnboarding";
import { pool } from "../../../constants";
import OnboardingSteps from "../../../components/Steps/OnboardingSteps";
import { API_URL } from "../../../constants";
import { useAuth } from "../../../provider";
import axios from "axios";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useLocation } from "react-router-dom";

import DiaCard from "../../../components/Dialog/DiaCard";
import CustomDialog from "../../../components/Dialog/DiaCompMedium"

import CoreBloodTestExplain from "./ShopCardsMarker/CoreBloodTestExplain";

import BioAgeCard from "./AddOnInfoCards/BioAgeCard"
import GutMicrobiomeCard from "./AddOnInfoCards/GutMicrobiomeCard";
import VitaminsCard from "./AddOnInfoCards/VitaminsCard";
import FemaleHormoneCard from "./AddOnInfoCards/FemaleHormoneCard";
import OvarianReserveCard from "./AddOnInfoCards/OvarianReserveCard";
import MaleHormoneCard from "./AddOnInfoCards/MaleHormoneCard";
import ProstateCard from "./AddOnInfoCards/ProstateCard";
import ThyroidCard from "./AddOnInfoCards/ThyroidCard";
import OmegaCard from "./AddOnInfoCards/OmegaCard";
import AtherosclerosisCard from "./AddOnInfoCards/AtherosclerosisCard";
import BonesCard from "./AddOnInfoCards/BonesCard";
import ColonCard from "./AddOnInfoCards/ColonCard";


import ButtonForm from "../../../components/Button/ButtonForm";
//import { loadStripe } from "@stripe/stripe-js";
import { PaymentIcon } from 'react-svg-credit-card-payment-icons';
import Apple_Pay from '../../../assets/Apple_Pay.svg'
import { useTranslation } from "../../../language";
//import LanguageToggle from "../../../language";

export default function OnboardingWelcome() {

const { state } = useAuth();
const location = useLocation();
const theme = useTheme();
const { t, changeLanguage } = useTranslation();

const [plans, setPlans] = useState([]);
const [upgradeSelected, setUpgradeSelected] = useState(false);

const [selectedAddOns, setSelectedAddOns] = useState([]);

const [coreOpen, setCoreOpen] = useState(false);
const [infoOpen, setInfoOpen] = useState(false);
const [infoContent, setInfoContent] = useState(null);

const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const [message, setMessage] = useState("");

useEffect(() => {
  if (state.user?.language) {
    changeLanguage(state.user.language);
  }
}, [state.user?.language]);

useEffect(() => {
    async function fetchPlans() {
      try {
        const response = await axios.get(`${API_URL}/api/subproducts`);
        const data = Array.isArray(response.data) ? response.data : [];
        setPlans(data);
      } catch (error) {
        console.error("Failed to fetch plans:", error);
        setPlans([]);
      } finally {
        setLoading(false);
      }
    }

    fetchPlans();
  }, []);

// API returns two mutually exclusive plans: plan_option === "2" → Membership + 2 tests (already bundled)
// plan_option === "3" → Membership + 4 tests (upgrade already included)
// Prices are final, no additions. “The checkbox doesn’t add anything — it switches plans.”
// UI checkbox just toggles which plan is chosen.
// Payload must send exactly one plan.

const basePlan = plans.find(p => p.plan_option === "2");     // 2 tests
const upgradePlan = plans.find(p => p.plan_option === "3"); // 4 tests
const selectedPlan = upgradeSelected ? upgradePlan : basePlan; // selected plan

// ---- Price calculation ----
const totalAnnualPrice = useMemo(() => {
  return selectedPlan ? Number(selectedPlan.price_eur) : 0;
}, [selectedPlan]);

const subscriptionPayload = {subscription_plan_id: selectedPlan?.plan_option,}; // Payload for API POST (FIXED)
  
// ------------------ Add-on definitions ------------------
//{ id: "age", name: t("add_bio"), price: 0, component: <BioAgeCard /> },
const addOnBlood = [
    { id: "age", name: t("add_bio"), price: 19, component: <BioAgeCard /> },
    { id: "vitamins", name: t("add_vit"), price: 419, component: <VitaminsCard /> },
    { id: "omega", name: t("add_omega"), price: 99, component: <OmegaCard /> },
    { id: "horm_female", name: t("add_hormf"), price: 149, component: <FemaleHormoneCard /> },
    { id: "horm_male", name: t("add_hormm"), price: 149, component: <MaleHormoneCard /> },
    { id: "psa", name: t("add_psa"), price: 19, component: <ProstateCard /> },
    { id: "thyroid", name: t("add_thyr"), price: 79, component: <ThyroidCard /> },
    { id: "atherosclerosis", name: t("add_ath"), price: 149, component: <AtherosclerosisCard /> },
    { id: "bones", name: t("add_bones"), price: 129, component: <BonesCard /> },
    { id: "fertility", name: t("add_ov"), price: 49, component: <OvarianReserveCard /> },
  ];

const addOnStool = [
    { id: "gutmicrobiome", name: t("add_gut"), price: 199, component: <GutMicrobiomeCard /> },
     { id: "ifob", name: t("add_fob"), price: 19, component: <ColonCard /> },
  ]

const allAddOns = [...addOnBlood, ...addOnStool];

const toggleAddOn = (id) => {
  setSelectedAddOns((prev) =>
    prev.includes(id)
      ? prev.filter((a) => a !== id)
      : [...prev, id]
  );
};


// ------------------ helper logic: Gender-aware Select All ------------------

const getSelectableAddOnIdsByGender = (gender) => {
  return allAddOns
    .filter((a) => {
      if (gender === "M") {
        return !["horm_female", "fertility"].includes(a.id);
      }
      if (gender === "F") {
        return !["horm_male", "psa"].includes(a.id);
      }
      return true;
    })
    .map((a) => a.id);
};

const handleSelectAllByGender = (gender) => {
  const selectableIds = getSelectableAddOnIdsByGender(gender);

  const allSelected = selectableIds.every((id) =>
    selectedAddOns.includes(id)
  );

  if (allSelected) {
    // deselect
    setSelectedAddOns((prev) =>
      prev.filter((id) => !selectableIds.includes(id))
    );
  } else {
    // select
    setSelectedAddOns((prev) => [
      ...new Set([...prev, ...selectableIds]),
    ]);
  }
};

//const selectableIds = getSelectableAddOnIds();
//const allSelected = selectableIds.every((id) =>
//  selectedAddOns.includes(id)
//);

const menIds = getSelectableAddOnIdsByGender("M");
const womenIds = getSelectableAddOnIdsByGender("F");

const allMenSelected =
  menIds.length > 0 && menIds.every(id => selectedAddOns.includes(id));

const allWomenSelected =
  womenIds.length > 0 && womenIds.every(id => selectedAddOns.includes(id));

// ------------------ Add-on calculate total ------------------
const addOnTotal = allAddOns
  .filter((a) => selectedAddOns.includes(a.id))
  .reduce((sum, a) => sum + a.price, 0);

// ------------------ Add-on section renderer ------------------
const AddOnSection = ({ title, icon: Icon, iconColor, items }) => (
  <Box sx={{ mt: 3 }}>
    <Box sx={{ px: { xs: 2, sm: 4 }, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{
        display: 'inline-flex',
        border: '1px solid',
        borderColor: theme.palette.greydark.C200,
        borderRadius: 4,
        px: 1.5,
        py: 0.5,
      }}>
        {Icon && (
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 26, height: 26, borderRadius: '50%',
            bgcolor: iconColor + '1a',
            flexShrink: 0,
          }}>
            <Icon size={14} color={iconColor} />
          </Box>
        )}
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Stack>
    </Box>

    <Box>
      {items.map((a, index) => (
        <Box
          key={a.id}
          sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            bgcolor: index % 2 === 1 ? 'rgba(0,0,0,0.008)' : 'rgba(0,0,0,0.035)',
            px: { xs: 2, sm: 4 },
            py: 0.75,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', sm: 'nowrap' }, alignItems: 'center', columnGap: 1 }}>
              <FormControlLabel
                sx={{ width: { xs: '100%', sm: 'auto' }, m: 0 }}
                control={
                  <Checkbox
                    checked={selectedAddOns.includes(a.id)}
                    onChange={() => toggleAddOn(a.id)}
                    sx={{
                      color: "greydark.C500",
                      "&.Mui-checked": { color: "greydark.C500" },
                      p: { xs: 0.5, sm: 1 },
                    }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {a.name}
                  </Typography>
                }
              />
              <Box sx={{ pl: { xs: '32px', sm: 0 }, display: 'flex', alignItems: 'center' }}>
                <Tooltip title={`More info about ${a.name}`}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setInfoContent(a.component);
                      setInfoOpen(true);
                    }}
                  >
                    <Info size={20} color={theme.palette.secondary.dark} />
                  </IconButton>
                </Tooltip>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  onClick={() => { setInfoContent(a.component); setInfoOpen(true); }}
                  sx={{ display: { xs: 'block', sm: 'none' }, cursor: 'pointer', userSelect: 'none' }}
                >
                  {t("addon_read_more")}
                </Typography>
              </Box>
            </Box>
          </Box>
          {selectedAddOns.includes(a.id) && (
            <Typography variant="body1" fontWeight={500} sx={{ flexShrink: 0, pl: 1 }}>
              € {a.price},-
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  </Box>
);
  
//------ CHECKOUT ------------

const formik = useFormik({
    initialValues: {
      email: state.user?.email || "",
      userId: state.user?.id || "",
      //firstName: state.user?.first_name || "",
      //lastName: state.user?.last_name || "",
      stripe_cust_id: state.user?.stripe_customer_id
    },
    validationSchema: Yup.object({
      email: Yup.string().email().required(),
      userId: Yup.string().required(),
      //firstName: Yup.string().required(),
      //lastName: Yup.string().required(),
      stripe_cust_id: Yup.string().required(),
    }),
    onSubmit: async (values) => {
      setError(null);
      setMessage(t("onb_msg_redirect"));
      setLoading(true);
      try {
        const stripe = await stripePromise;
        // === 1️⃣ Create subscription checkout session ===
        const payload = {
          email: values.email,
          userId: values.userId,
          custid: values.stripe_cust_id,
          planOption: selectedPlan.plan_option, // send selected plan option (2 or 3)
          addons: selectedAddOns.map(id => ({
            sku: id,                       // or product_id
            quantity: 1,
          })),
        };
        console.log (payload)
        const Response = await axios.post(`${API_URL}/api/stripe/create-chained-checkout/`, payload);
        const SessionId = Response.data.sessionId;
        const sessionUrl = Response.data.url;
        
        // Stripe best practice: some servers return session.url
        if (sessionUrl) {
          window.location.href = sessionUrl;
          return;
          }
       
        // === 3️⃣ Redirect to Stripe (subscription checkout takes precedence) ===
        const result = await stripe.redirectToCheckout({ sessionId: SessionId });

        if (result.error) {
          console.error(result.error.message);
          setError(t("onb_error_stripe"));
        }

      } catch (error) {
        console.error(error.response?.data || error.message)
        setError(t("onb_error_checkout"));
       } finally {
      setLoading(false);
    }
    },
  });

  return (

  <PageProtectedOnboarding
      headerImage={pool}
      brandtext="Preventive Health Intelligence"
      content={
        <>
          

           {/* Header */}
          <Grid container sx={{ mb: 6, display:'flex', justifyContent: 'center', alignItems:'center'}}>
            <Grid size={12} sx={{ width: '100%', maxWidth: 900, px: { xs: 1, sm: 2 }, display:'flex', justifyContent: 'center', alignItems:'center' }}>
              <OnboardingSteps stepNumber={0} />
            </Grid>
          </Grid>

          {/* Explain status */}
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2, px: { xs: 2, sm: 0 } }}>
            <Box component="ul" sx={{ mb: 4, pl: 2.5, maxWidth: 700 }}>
              {[t("onb_start1"), t("onb_start2"), t("onb_start3")].map((item, i) => (
                <Typography key={i} component="li" variant="subtitle1" sx={{ fontWeight: 400, mb: 0.5 }}>
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* === Unified Cart === */}
          <Grid container justifyContent="center">
            <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 800,
                  mb: 4,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: theme.palette.greydark.C200,
                  overflow: 'hidden',
                }}
              >
                {/* Header */}
                <Box sx={{ px: { xs: 2, sm: 4 }, py: 2.5, borderBottom: '1px solid', borderColor: theme.palette.greydark.C200 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{
                      width: 26, height: 26, borderRadius: '50%',
                      bgcolor: theme.palette.primary.main, color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.8rem', fontWeight: 700, flexShrink: 0,
                    }}>1</Box>
                    <ShoppingCart size={20} />
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {t("onb_subscrconf")}
                      </Typography>
                      <Typography variant="h6" fontWeight={400} color="text.secondary" sx={{ fontSize: { sm: '0.875rem' } }}>
                        {t("onb_subscrconf_sub")}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Membership item */}
                <Box sx={{ px: { xs: 2, sm: 4 }, py: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="h6" fontWeight={600}>
                    {t("onb_subscr1")}
                  </Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight={500}>
                      € 108,-
                    </Typography>
                  </Box>
                </Box>

                {/* 2 Tests item */}
                <Box sx={{ px: { xs: 2, sm: 4 }, py: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {t("onb_subscr5")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        € 189,-&nbsp;{t("onb_per_test")} 
                      </Typography>
                    </Box>
                    <Tooltip title="More Info about Core Health Blood Test" placement="right">
                      <IconButton size="small" onClick={() => setCoreOpen(true)}>
                        <Info size={18} color={theme.palette.secondary.dark} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight={500}>
                      € 378,-
                    </Typography>
                  </Box>
                </Box>

                {/* Upgrade item */}
                <Box sx={{ px: { xs: 2, sm: 4 }, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={upgradeSelected}
                      onChange={(e) => setUpgradeSelected(e.target.checked)}
                      sx={{ color: "greydark.C500", "&.Mui-checked": { color: "greydark.C500" }, p: { xs: 0.5, sm: 1 } }}
                    />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t("onb_subscr71")}
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {t("onb_subscr71_detail")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        € 170,-&nbsp;{t("onb_per_test2")} 
                      </Typography>
                    </Box>
                  </Stack>
                  {upgradeSelected && <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight={500}>
                      € 340,-
                    </Typography>
                  </Box>}
                </Box>

                {/* Total footer */}
                <Box
                  sx={{
                    px: { xs: 2, sm: 4 },
                    py: 2.5,
                    bgcolor: 'grey.C50',
                    borderTop: '1px solid',
                    borderColor: theme.palette.greydark.C200,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="h6" fontWeight={600}>
                    {t("onb_subscr10")}
                  </Typography>
                  <Box sx={{ textAlign: 'right', ml: 'auto' }}>
                    <Typography variant="h5" fontWeight={400}>
                      € {totalAnnualPrice.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                      {t("onb_subscr11")}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>


        {/* === Unified Cart ADD ONs === */}
          <Grid container justifyContent="center">
            <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 800,
                  mb: 4,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: theme.palette.greydark.C200,
                  overflow: 'hidden',
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    px: { xs: 2, sm: 4 },
                    py: { xs: 2, sm: 2.5 },
                    borderBottom: '1px solid',
                    borderColor: theme.palette.greydark.C200,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{
                      width: 26, height: 26, borderRadius: '50%',
                      bgcolor: theme.palette.primary.main, color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.8rem', fontWeight: 700, flexShrink: 0,
                    }}>2</Box>
                    <ShoppingCart size={20} />
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {t("onb_subscr12")}
                      </Typography>
                      <Typography variant="h6" fontWeight={400} color="text.secondary" sx={{ fontSize: { sm: '0.875rem' } }}>
                        {t("onb_subscr12_sub")}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Chip
                      label={allMenSelected ? t("deselect_all_men") : t("select_all_men")}
                      onClick={() => handleSelectAllByGender("M")}
                      size="small"
                      variant={allMenSelected ? "filled" : "outlined"}
                      icon={allMenSelected ? <Check size={13} /> : undefined}
                      sx={{ fontSize: '0.72rem', fontWeight: 500 }}
                    />
                    <Chip
                      label={allWomenSelected ? t("deselect_all_women") : t("select_all_women")}
                      onClick={() => handleSelectAllByGender("F")}
                      size="small"
                      variant={allWomenSelected ? "filled" : "outlined"}
                      icon={allWomenSelected ? <Check size={13} /> : undefined}
                      sx={{ fontSize: '0.72rem', fontWeight: 500 }}
                    />
                  </Stack>
                </Box>

                {/* Add-on lists */}
                <Box sx={{ py: 2 }}>
                  <AddOnSection title={t("onb_subscr13")} icon={Droplets} iconColor="#e53935" items={addOnBlood} />
                  <AddOnSection title={t("onb_subscr14")} icon={FlaskConical} iconColor="#8d6e63" items={addOnStool} />

                  <DiaCard
                    open={infoOpen}
                    onClose={() => setInfoOpen(false)}
                    title=""
                    content={
                      <Grid container>
                        <Grid size={12} sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                          {infoContent}
                        </Grid>
                      </Grid>
                    }
                  />

                  <CustomDialog
                    open={coreOpen}
                    onClose={() => setCoreOpen(false)}
                    title=""
                    content={
                      <Grid container>
                        <Grid size={12} sx={{ display: "flex", justifyContent: "center" }}>
                          <CoreBloodTestExplain />
                        </Grid>
                      </Grid>
                    }
                  />
                </Box>

                {/* Total footer */}
                <Box
                  sx={{
                    px: { xs: 2, sm: 4 },
                    py: 2.5,
                    bgcolor: 'grey.C50',
                    borderTop: '1px solid',
                    borderColor: theme.palette.greydark.C200,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="h6" fontWeight={600}>
                    {t("onb_subscr15")}
                  </Typography>
                  <Box sx={{ textAlign: 'right', ml: 'auto' }}>
                    <Typography variant="h5" fontWeight={400}>
                      € {addOnTotal.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                      {t("onb_subscr16")}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>


{/* Cart + Stripe */}
                  {loading ? (
                    <Grid container sx={{justifyContent: 'center', mt: 4}}>
                      <CircularProgress size={24} />
                    </Grid>
                  ) : (
                    <Grid container spacing={2} sx={{mt: 2, mb:32, justifyContent: 'center'}}>

                     
                    
                    <Grid size={12} sx={{display: 'flex', justifyContent: 'center'}}>
                      <Typography variant="h6" textAlign="center" sx={{fontWeight:600}}>
                        {t("onb_subscr165")}<br /> <br /> 
                        {t("onb_subscr17")} 
                      </Typography>
                      </Grid>
                       <Grid size={12} sx={{display: 'flex', justifyContent: 'center', mb: 2}}>
                        <Typography variant="h6" textAlign="center" sx={{fontWeight:400}}>
                        {t("onb_subscr18")} {totalAnnualPrice.toFixed(2)} {t("onb_subscr19")} <br />  
                        {t("onb_subscr20")} { addOnTotal.toFixed(2) } {t("onb_subscr21")} </Typography>
                    </Grid>

                     <Grid size={12} sx={{display: 'flex', justifyContent: 'center', mb: 2}}>
                        <form onSubmit={formik.handleSubmit}>
                          <ButtonForm name={t("onb_btn_secure_payment")} type="submit" disabled={loading} />
                        </form>
                      </Grid>

                     <Grid size={12} sx={{display: 'flex', justifyContent: 'center', mb: 4}}>
                        <Stack direction="row" spacing={2}>
                          <PaymentIcon type="visa" format="flatRounded" width={40} />
                          <PaymentIcon type="mastercard" format="flatRounded" width={40} />
                          <PaymentIcon type="paypal" format="flatRounded" width={40} />
                          <Box mb={1}>
                          <div><img src={Apple_Pay} alt="ApplePay" width={40} height={30} /></div>
                          </Box>
                        </Stack>
                      </Grid>

                      {error && (
                        <Grid size={12} sx={{maxWidth: 600, mx: 'auto'}}>
                          <Alert severity="error">{error}</Alert>
                        </Grid>
                      )}
                      
                      {message && (
                        <Grid size={12} sx={{maxWidth: 600, mx: 'auto'}}>
                          <Alert severity="info">{message}</Alert>
                        </Grid>
                      )}
                    </Grid>
                  )
                }







        </>
      }
    />
  );
}